/**
 * AxLabs Chemistry Experiments - Physical Chemistry
 *
 * Collection of physical chemistry experiments generated using
 * the declarative framework.
 */
import { createExperimentFromSchema, IDEAL_GAS_CONSTANT, STANDARD_PRESSURE } from '../../framework/index.js';
import { getParam, getState, getStateValues } from '../../physics/generated/helpers.js';
// ============================================================================
// VAPOR PRESSURE (CLAUSIUS-CLAPEYRON)
// ============================================================================
export const VaporPressureClausiusClapeyron = createExperimentFromSchema({
    id: 'vapor-pressure-clausius-clapeyron',
    name: 'Vapor Pressure (Clausius-Clapeyron)',
    domain: 'chemistry',
    description: 'Temperature dependence of vapor pressure using the Clausius-Clapeyron equation',
    conceptTags: ['Phase Equilibrium', 'Vapor Pressure', 'Thermodynamics', 'Clausius-Clapeyron Equation'],
    difficulty: 5,
    stateVariables: [
        { id: 'T', name: 'Temperature', unit: 'K', initial: 298.15, symbol: 'T', description: 'System temperature' },
    ],
    parameters: [
        { id: 'DeltaH_vap', name: 'Enthalpy of Vaporization', unit: 'J/mol', min: 10000, max: 100000, default: 40660, symbol: '\\Delta H_{vap}', description: 'Enthalpy change for vaporization' },
        { id: 'T_ref', name: 'Reference Temperature', unit: 'K', min: 273, max: 400, default: 373.15, symbol: 'T_{ref}', description: 'Reference temperature (e.g., boiling point)' },
        { id: 'P_ref', name: 'Reference Pressure', unit: 'Pa', min: 1000, max: 200000, default: 101325, symbol: 'P_{ref}', description: 'Vapor pressure at reference temperature' },
    ],
    equations: [
        {
            id: 'claussius-clapeyron',
            name: 'Clausius-Clapeyron Equation',
            latex: '\\ln\\left(\\frac{P}{P_{ref}}\\right) = -\\frac{\\Delta H_{vap}}{R}\\left(\\frac{1}{T} - \\frac{1}{T_{ref}}\\right)',
            description: 'Relationship between vapor pressure and temperature',
        },
        {
            id: 'vapor-pressure',
            name: 'Vapor Pressure',
            latex: 'P(T) = P_{ref}\\exp\\left[-\\frac{\\Delta H_{vap}}{R}\\left(\\frac{1}{T} - \\frac{1}{T_{ref}}\\right)\\right]',
            description: 'Vapor pressure as a function of temperature',
        },
    ],
    derivatives: (_state, _params) => ({
        T: 0,
    }),
    derivedState: (state, params) => {
        const T = getState(state, 'T');
        const DeltaH_vap = getParam(params, 'DeltaH_vap');
        const T_ref = getParam(params, 'T_ref');
        const P_ref = getParam(params, 'P_ref');
        const R = IDEAL_GAS_CONSTANT;
        const P_vap = P_ref * Math.exp(-DeltaH_vap / R * (1 / T - 1 / T_ref));
        const ln_P = Math.log(P_vap);
        return {
            P_vapor: P_vap,
            ln_P_vapor: ln_P,
            inv_T: 1 / T,
        };
    },
    failureModes: [
        {
            id: 'too-cold',
            name: 'Temperature Below Freezing',
            check: (_state, params) => {
                const T = getState(getStateValues(_state), 'T');
                const T_ref = getParam(params, 'T_ref');
                return T < T_ref - 150;
            },
            severity: 'warning',
            educationalMessage: 'Temperature is very low. The Clausius-Clapeyron equation assumes ideal gas behavior and constant enthalpy of vaporization, which may not hold at these conditions.',
        },
        {
            id: 'supercritical',
            name: 'Approaching Critical Point',
            check: (_state, _params) => {
                const T = getState(getStateValues(_state), 'T');
                return T > 600;
            },
            severity: 'warning',
            educationalMessage: 'Temperature is approaching the critical point. The distinction between liquid and gas phases becomes less meaningful.',
        },
    ],
    bounds: {
        T: { min: 200, max: 600 },
    },
});
// ============================================================================
// RAOULT'S LAW (IDEAL MIXTURE)
// ============================================================================
export const RaoultsLawIdealMixture = createExperimentFromSchema({
    id: 'raoults-law-ideal-mixture',
    name: "Raoult's Law (Ideal Mixture)",
    domain: 'chemistry',
    description: 'Vapor pressure of ideal binary liquid mixtures',
    conceptTags: ['Raoult\'s Law', 'Ideal Solutions', 'Partial Pressure', 'Mole Fraction'],
    difficulty: 4,
    stateVariables: [
        { id: 'x_A', name: 'Mole Fraction A', unit: '', initial: 0.5, symbol: 'x_A', description: 'Mole fraction of component A in liquid phase' },
    ],
    parameters: [
        { id: 'P_vap_A', name: 'Vapor Pressure Pure A', unit: 'Pa', min: 1000, max: 200000, default: 50000, symbol: 'P_A^*', description: 'Vapor pressure of pure component A' },
        { id: 'P_vap_B', name: 'Vapor Pressure Pure B', unit: 'Pa', min: 1000, max: 200000, default: 80000, symbol: 'P_B^*', description: 'Vapor pressure of pure component B' },
    ],
    equations: [
        {
            id: 'raoults-law-a',
            name: "Raoult's Law Component A",
            latex: 'P_A = x_A P_A^*',
            description: 'Partial pressure of component A',
        },
        {
            id: 'raoults-law-b',
            name: "Raoult's Law Component B",
            latex: 'P_B = x_B P_B^* = (1 - x_A)P_B^*',
            description: 'Partial pressure of component B',
        },
        {
            id: 'total-pressure',
            name: 'Total Vapor Pressure',
            latex: 'P_{total} = P_A + P_B = x_A P_A^* + (1 - x_A)P_B^*',
            description: 'Total vapor pressure above the mixture',
        },
    ],
    derivatives: (_state, _params) => ({
        x_A: 0,
    }),
    derivedState: (state, params) => {
        const x_A = getState(state, 'x_A');
        const x_B = 1 - x_A;
        const P_vap_A = getParam(params, 'P_vap_A');
        const P_vap_B = getParam(params, 'P_vap_B');
        const P_A = x_A * P_vap_A;
        const P_B = x_B * P_vap_B;
        const P_total = P_A + P_B;
        return {
            x_B,
            P_A,
            P_B,
            P_total,
            y_A: P_A / P_total,
            y_B: P_B / P_total,
        };
    },
    failureModes: [
        {
            id: 'invalid-mole-fraction',
            name: 'Invalid Mole Fraction',
            check: (_state, _params) => {
                const x_A = getState(getStateValues(_state), 'x_A');
                return x_A < 0 || x_A > 1;
            },
            severity: 'error',
            educationalMessage: 'Mole fraction must be between 0 and 1. x_A + x_B = 1 for a binary mixture.',
        },
        {
            id: 'non-ideal-deviation',
            name: 'Potential Non-Ideal Behavior',
            check: (_state, params) => {
                const P_vap_A = getParam(params, 'P_vap_A');
                const P_vap_B = getParam(params, 'P_vap_B');
                return Math.abs(P_vap_A - P_vap_B) > 100000;
            },
            severity: 'warning',
            educationalMessage: 'Large difference in pure vapor pressures may indicate significant deviations from ideal behavior. Activity coefficients may be needed.',
        },
    ],
    bounds: {
        x_A: { min: 0, max: 1 },
    },
});
// ============================================================================
// BOILING POINT ELEVATION
// ============================================================================
export const BoilingPointElevation = createExperimentFromSchema({
    id: 'boiling-point-elevation',
    name: 'Boiling Point Elevation',
    domain: 'chemistry',
    description: 'Effect of solute concentration on boiling point of a solvent',
    conceptTags: ['Colligative Properties', 'Boiling Point Elevation', 'Molality', 'Van\'t Hoff Factor'],
    difficulty: 4,
    stateVariables: [
        { id: 'm', name: 'Molality', unit: 'mol/kg', initial: 0.1, symbol: 'm', description: 'Molal concentration of solute' },
    ],
    parameters: [
        { id: 'K_b', name: 'Ebullioscopic Constant', unit: 'K·kg/mol', min: 0.1, max: 10, default: 0.512, symbol: 'K_b', description: 'Boiling point elevation constant (water: 0.512 K·kg/mol)' },
        { id: 'i', name: 'Van\'t Hoff Factor', unit: '', min: 1, max: 4, default: 2, symbol: 'i', description: 'Number of particles per formula unit' },
        { id: 'T_b_pure', name: 'Pure Solvent Boiling Point', unit: 'K', min: 200, max: 400, default: 373.15, symbol: 'T_b^*', description: 'Boiling point of pure solvent' },
    ],
    equations: [
        {
            id: 'boiling-point-elevation',
            name: 'Boiling Point Elevation',
            latex: '\\Delta T_b = i K_b m',
            description: 'Increase in boiling point due to solute',
        },
        {
            id: 'new-boiling-point',
            name: 'New Boiling Point',
            latex: 'T_b = T_b^* + \\Delta T_b',
            description: 'Boiling point of the solution',
        },
    ],
    derivatives: (_state, _params) => ({
        m: 0,
    }),
    derivedState: (state, params) => {
        const m = getState(state, 'm');
        const K_b = getParam(params, 'K_b');
        const i = getParam(params, 'i');
        const T_b_pure = getParam(params, 'T_b_pure');
        const Delta_T_b = i * K_b * m;
        const T_b = T_b_pure + Delta_T_b;
        return {
            Delta_T_b,
            T_b,
        };
    },
    failureModes: [
        {
            id: 'negative-molality',
            name: 'Negative Molality',
            check: (_state, _params) => {
                const m = getState(getStateValues(_state), 'm');
                return m < 0;
            },
            severity: 'error',
            educationalMessage: 'Molality cannot be negative. Concentration must be a positive value.',
        },
        {
            id: 'high-concentration',
            name: 'High Concentration Warning',
            check: (_state, _params) => {
                const m = getState(getStateValues(_state), 'm');
                return m > 5;
            },
            severity: 'warning',
            educationalMessage: 'At high concentrations, the solution may deviate significantly from ideal behavior. The linear relationship ΔT_b = iK_bm may not hold accurately.',
        },
    ],
    bounds: {
        m: { min: 0, max: 10 },
    },
});
// ============================================================================
// FREEZING POINT DEPRESSION
// ============================================================================
export const FreezingPointDepression = createExperimentFromSchema({
    id: 'freezing-point-depression',
    name: 'Freezing Point Depression',
    domain: 'chemistry',
    description: 'Effect of solute concentration on freezing point of a solvent',
    conceptTags: ['Colligative Properties', 'Freezing Point Depression', 'Molality', 'Van\'t Hoff Factor'],
    difficulty: 4,
    stateVariables: [
        { id: 'm', name: 'Molality', unit: 'mol/kg', initial: 0.5, symbol: 'm', description: 'Molal concentration of solute' },
    ],
    parameters: [
        { id: 'K_f', name: 'Cryoscopic Constant', unit: 'K·kg/mol', min: 0.1, max: 40, default: 1.86, symbol: 'K_f', description: 'Freezing point depression constant (water: 1.86 K·kg/mol)' },
        { id: 'i', name: 'Van\'t Hoff Factor', unit: '', min: 1, max: 4, default: 2, symbol: 'i', description: 'Number of particles per formula unit' },
        { id: 'T_f_pure', name: 'Pure Solvent Freezing Point', unit: 'K', min: 200, max: 300, default: 273.15, symbol: 'T_f^*', description: 'Freezing point of pure solvent' },
    ],
    equations: [
        {
            id: 'freezing-point-depression',
            name: 'Freezing Point Depression',
            latex: '\\Delta T_f = i K_f m',
            description: 'Decrease in freezing point due to solute',
        },
        {
            id: 'new-freezing-point',
            name: 'New Freezing Point',
            latex: 'T_f = T_f^* - \\Delta T_f',
            description: 'Freezing point of the solution',
        },
    ],
    derivatives: (_state, _params) => ({
        m: 0,
    }),
    derivedState: (state, params) => {
        const m = getState(state, 'm');
        const K_f = getParam(params, 'K_f');
        const i = getParam(params, 'i');
        const T_f_pure = getParam(params, 'T_f_pure');
        const Delta_T_f = i * K_f * m;
        const T_f = T_f_pure - Delta_T_f;
        return {
            Delta_T_f,
            T_f,
        };
    },
    failureModes: [
        {
            id: 'negative-molality',
            name: 'Negative Molality',
            check: (_state, _params) => {
                const m = getState(getStateValues(_state), 'm');
                return m < 0;
            },
            severity: 'error',
            educationalMessage: 'Molality cannot be negative.',
        },
        {
            id: 'excessive-depression',
            name: 'Excessive Freezing Point Depression',
            check: (_state, params) => {
                const m = getState(getStateValues(_state), 'm');
                const K_f = getParam(params, 'K_f');
                const i = getParam(params, 'i');
                return i * K_f * m > 100;
            },
            severity: 'warning',
            educationalMessage: 'The freezing point depression is very large. At such low temperatures, the solvent may freeze completely or the solution may become supersaturated.',
        },
    ],
    bounds: {
        m: { min: 0, max: 20 },
    },
});
// ============================================================================
// OSMOTIC PRESSURE
// ============================================================================
export const OsmoticPressure = createExperimentFromSchema({
    id: 'osmotic-pressure',
    name: 'Osmotic Pressure',
    domain: 'chemistry',
    description: 'Osmotic pressure across a semipermeable membrane (van\'t Hoff equation)',
    conceptTags: ['Osmosis', 'Osmotic Pressure', 'Semipermeable Membrane', 'Van\'t Hoff Equation'],
    difficulty: 5,
    stateVariables: [
        { id: 'C', name: 'Solute Concentration', unit: 'mol/L', initial: 0.1, symbol: 'C', description: 'Molar concentration of solute' },
    ],
    parameters: [
        { id: 'i', name: 'Van\'t Hoff Factor', unit: '', min: 1, max: 4, default: 1, symbol: 'i', description: 'Number of particles per formula unit' },
        { id: 'T', name: 'Temperature', unit: 'K', min: 273, max: 400, default: 298.15, symbol: 'T', description: 'System temperature' },
    ],
    equations: [
        {
            id: 'vant-hoff-equation',
            name: 'van\'t Hoff Equation',
            latex: '\\Pi = iCRT',
            description: 'Osmotic pressure equation',
        },
        {
            id: 'osmotic-pressure-altitude',
            name: 'Osmotic Pressure as Hydrostatic Height',
            latex: 'h = \\frac{\\Pi}{\\rho g}',
            description: 'Equivalent column of solvent (water)',
        },
    ],
    derivatives: (_state, _params) => ({
        C: 0,
    }),
    derivedState: (state, params) => {
        const C = getState(state, 'C');
        const i = getParam(params, 'i');
        const T = getParam(params, 'T');
        const R = IDEAL_GAS_CONSTANT;
        const Pi = i * C * R * T;
        return {
            Pi,
            Pi_atm: Pi / STANDARD_PRESSURE,
            Pi_torr: Pi * 0.0075,
        };
    },
    failureModes: [
        {
            id: 'negative-concentration',
            name: 'Negative Concentration',
            check: (_state, _params) => {
                const C = getState(getStateValues(_state), 'C');
                return C < 0;
            },
            severity: 'error',
            educationalMessage: 'Concentration cannot be negative.',
        },
        {
            id: 'high-concentration',
            name: 'High Concentration',
            check: (_state, _params) => {
                const C = getState(getStateValues(_state), 'C');
                return C > 5;
            },
            severity: 'warning',
            educationalMessage: 'At high concentrations, intermolecular interactions become significant and the ideal solution approximation (Π = iCRT) may break down.',
        },
    ],
    bounds: {
        C: { min: 0, max: 10 },
    },
});
// ============================================================================
// HENRY'S LAW (GAS SOLUBILITY)
// ============================================================================
export const HenrysLaw = createExperimentFromSchema({
    id: 'henrys-law',
    name: "Henry's Law (Gas Solubility)",
    domain: 'chemistry',
    description: 'Solubility of a gas in a liquid as a function of pressure',
    conceptTags: ['Henry\'s Law', 'Gas Solubility', 'Partial Pressure', 'Equilibrium'],
    difficulty: 4,
    stateVariables: [
        { id: 'P_gas', name: 'Gas Partial Pressure', unit: 'atm', initial: 1, symbol: 'P_{gas}', description: 'Partial pressure of the gas above the liquid' },
    ],
    parameters: [
        { id: 'k_H', name: "Henry's Law Constant", unit: 'mol/(L·atm)', min: 0.0001, max: 0.1, default: 0.0013, symbol: 'k_H', description: 'Solubility constant (O₂ in water at 25°C: 0.0013 mol/L·atm)' },
    ],
    equations: [
        {
            id: 'henrys-law',
            name: "Henry's Law",
            latex: 'C = k_H P_{gas}',
            description: 'Dissolved gas concentration is proportional to partial pressure',
        },
        {
            id: 'bunsen-coefficient',
            name: 'Bunsen Solubility Coefficient',
            latex: '\\alpha = \\frac{V_{gas}}{V_{liquid} \\cdot P}',
            description: 'Volume of gas (STP) dissolved per unit volume of liquid per atmosphere',
        },
    ],
    derivatives: (_state, _params) => ({
        P_gas: 0,
    }),
    derivedState: (state, params) => {
        const P_gas = getState(state, 'P_gas');
        const k_H = getParam(params, 'k_H');
        const C = k_H * P_gas;
        return {
            C,
            C_mM: C * 1000,
            C_ppm: C * 1000 * 32, // Assuming O2, convert to ppm by mass
        };
    },
    failureModes: [
        {
            id: 'negative-pressure',
            name: 'Negative Pressure',
            check: (_state, _params) => {
                const P_gas = getState(getStateValues(_state), 'P_gas');
                return P_gas < 0;
            },
            severity: 'error',
            educationalMessage: 'Partial pressure cannot be negative.',
        },
        {
            id: 'high-pressure',
            name: 'High Pressure Warning',
            check: (_state, _params) => {
                const P_gas = getState(getStateValues(_state), 'P_gas');
                return P_gas > 10;
            },
            severity: 'warning',
            educationalMessage: 'At high pressures, deviations from Henry\'s law may occur due to gas non-ideality and changes in solvent structure.',
        },
    ],
    bounds: {
        P_gas: { min: 0, max: 100 },
    },
});
// ============================================================================
// PHASE DIAGRAM (WATER-LIKE)
// ============================================================================
export const PhaseDiagramWaterLike = createExperimentFromSchema({
    id: 'phase-diagram-water-like',
    name: 'Phase Diagram (Water-Like)',
    domain: 'chemistry',
    description: 'Phase behavior of water-like substance with solid-liquid-gas phases',
    conceptTags: ['Phase Diagram', 'Triple Point', 'Critical Point', 'Phase Boundaries'],
    difficulty: 6,
    stateVariables: [
        { id: 'T', name: 'Temperature', unit: 'K', initial: 298.15, symbol: 'T', description: 'System temperature' },
        { id: 'P', name: 'Pressure', unit: 'Pa', initial: 101325, symbol: 'P', description: 'System pressure' },
    ],
    parameters: [
        { id: 'T_triple', name: 'Triple Point Temperature', unit: 'K', min: 200, max: 300, default: 273.16, symbol: 'T_{triple}' },
        { id: 'P_triple', name: 'Triple Point Pressure', unit: 'Pa', min: 100, max: 1000, default: 611.66, symbol: 'P_{triple}' },
        { id: 'T_critical', name: 'Critical Temperature', unit: 'K', min: 400, max: 800, default: 647.1, symbol: 'T_{critical}' },
        { id: 'P_critical', name: 'Critical Pressure', unit: 'Pa', min: 10000000, max: 30000000, default: 22064000, symbol: 'P_{critical}' },
        { id: 'DeltaH_fusion', name: 'Enthalpy of Fusion', unit: 'J/mol', min: 1000, max: 15000, default: 6010, symbol: '\\Delta H_{fusion}' },
    ],
    equations: [
        {
            id: 'clausius-clapeyron-solid-gas',
            name: 'Sublimation Curve',
            latex: '\\ln P = -\\frac{\\Delta H_{sub}}{R} \\cdot \\frac{1}{T} + C',
            description: 'Phase boundary between solid and gas',
        },
        {
            id: 'clausius-clapeyron-liquid-gas',
            name: 'Vaporization Curve',
            latex: '\\ln P = -\\frac{\\Delta H_{vap}}{R} \\cdot \\frac{1}{T} + C',
            description: 'Phase boundary between liquid and gas',
        },
    ],
    derivatives: (_state, _params) => ({
        T: 0,
        P: 0,
    }),
    derivedState: (state, params) => {
        const T = getState(state, 'T');
        const P = getState(state, 'P');
        const T_triple = getParam(params, 'T_triple');
        const P_triple = getParam(params, 'P_triple');
        const T_critical = getParam(params, 'T_critical');
        const P_critical = getParam(params, 'P_critical');
        const DeltaH_fusion = getParam(params, 'DeltaH_fusion');
        const R = IDEAL_GAS_CONSTANT;
        // Simplified phase boundary calculations
        // Liquid-gas boundary (approximated)
        const DeltaH_vap = 40660; // J/mol for water
        const P_liquid_gas = P_triple * Math.exp((DeltaH_vap / R) * (1 / T_triple - 1 / T));
        // Solid-gas boundary
        const DeltaH_sub = DeltaH_fusion + DeltaH_vap;
        const P_solid_gas = P_triple * Math.exp((DeltaH_sub / R) * (1 / T_triple - 1 / T));
        // Solid-liquid boundary (simplified as nearly vertical)
        const dP_dT_solid_liquid = -15000000; // Negative for water (unusual!)
        const P_solid_liquid = P_triple + dP_dT_solid_liquid * (T - T_triple);
        // Determine current phase (numeric codes)
        let phase_code = 0; // 0=liquid, 1=solid, 2=gas, 3=supercritical
        if (T < T_triple) {
            phase_code = P > P_solid_gas ? 1 : 2;
        }
        else if (T < T_critical) {
            phase_code = P > P_liquid_gas ? 0 : 2;
        }
        else {
            phase_code = P > P_critical ? 3 : 2;
        }
        // Check near triple point
        const nearTriplePoint = Math.abs(T - T_triple) < 1 && Math.abs(P - P_triple) < 100 ? 1 : 0;
        return {
            phase_code,
            nearTriplePoint,
            P_liquid_gas,
            P_solid_gas,
            P_solid_liquid,
            reduced_temperature: T / T_critical,
            reduced_pressure: P / P_critical,
        };
    },
    failureModes: [
        {
            id: 'invalid-temperature',
            name: 'Invalid Temperature',
            check: (_state, _params) => {
                const T = getState(getStateValues(_state), 'T');
                return T <= 0;
            },
            severity: 'error',
            educationalMessage: 'Temperature must be greater than 0 K.',
        },
        {
            id: 'negative-pressure',
            name: 'Negative Pressure',
            check: (_state, _params) => {
                const P = getState(getStateValues(_state), 'P');
                return P < 0;
            },
            severity: 'error',
            educationalMessage: 'Pressure cannot be negative.',
        },
    ],
    bounds: {
        T: { min: 200, max: 800 },
        P: { min: 1, max: 30000000 },
    },
});
// ============================================================================
// COLLIGATIVE PROPERTIES
// ============================================================================
export const ColligativeProperties = createExperimentFromSchema({
    id: 'colligative-properties',
    name: 'Colligative Properties (Complete)',
    domain: 'chemistry',
    description: 'All four colligative properties of solutions based on solute concentration',
    conceptTags: ['Colligative Properties', 'Boiling Point', 'Freezing Point', 'Osmotic Pressure', 'Vapor Pressure Lowering'],
    difficulty: 6,
    stateVariables: [
        { id: 'm', name: 'Molality', unit: 'mol/kg', initial: 0.5, symbol: 'm', description: 'Molal concentration of solute' },
    ],
    parameters: [
        { id: 'i', name: 'Van\'t Hoff Factor', unit: '', min: 1, max: 4, default: 2, symbol: 'i' },
        { id: 'K_b', name: 'Ebullioscopic Constant', unit: 'K·kg/mol', min: 0.1, max: 10, default: 0.512, symbol: 'K_b' },
        { id: 'K_f', name: 'Cryoscopic Constant', unit: 'K·kg/mol', min: 0.1, max: 40, default: 1.86, symbol: 'K_f' },
        { id: 'T_b_pure', name: 'Pure Boiling Point', unit: 'K', min: 300, max: 400, default: 373.15, symbol: 'T_b^*' },
        { id: 'T_f_pure', name: 'Pure Freezing Point', unit: 'K', min: 250, max: 300, default: 273.15, symbol: 'T_f^*' },
        { id: 'P_vap_pure', name: 'Pure Vapor Pressure', unit: 'Pa', min: 1000, max: 100000, default: 2338.8, symbol: 'P^*', description: 'Vapor pressure of pure solvent at 20°C' },
        { id: 'T', name: 'Temperature', unit: 'K', min: 273, max: 400, default: 293.15, symbol: 'T' },
    ],
    equations: [
        {
            id: 'vapor-pressure-lowering',
            name: 'Raoult\'s Law (Vapor Pressure Lowering)',
            latex: '\\Delta P = x_{solute} \\cdot P^*',
            description: 'Lowering of vapor pressure due to solute',
        },
        {
            id: 'boiling-elevation',
            name: 'Boiling Point Elevation',
            latex: '\\Delta T_b = i K_b m',
            description: 'Increase in boiling point',
        },
        {
            id: 'freezing-depression',
            name: 'Freezing Point Depression',
            latex: '\\Delta T_f = i K_f m',
            description: 'Decrease in freezing point',
        },
        {
            id: 'osmotic-pressure',
            name: 'Osmotic Pressure',
            latex: '\\Pi = i C R T \\approx \\frac{i m \\rho_{solvent} RT}{1 + m M_{solute}}',
            description: 'Osmotic pressure across semipermeable membrane',
        },
    ],
    derivatives: (_state, _params) => ({
        m: 0,
    }),
    derivedState: (state, params) => {
        const m = getState(state, 'm');
        const i = getParam(params, 'i');
        const K_b = getParam(params, 'K_b');
        const K_f = getParam(params, 'K_f');
        const T_b_pure = getParam(params, 'T_b_pure');
        const T_f_pure = getParam(params, 'T_f_pure');
        const P_vap_pure = getParam(params, 'P_vap_pure');
        const T = getParam(params, 'T');
        const R = IDEAL_GAS_CONSTANT;
        // Assume solvent is water (M_solvent = 0.018 kg/mol)
        const M_solvent = 0.018;
        const n_solute_per_kg = m;
        const n_solvent_per_kg = 1 / M_solvent;
        const x_solute = n_solute_per_kg / (n_solute_per_kg + n_solvent_per_kg);
        const x_solvent = 1 - x_solute;
        const Delta_P = x_solute * P_vap_pure;
        const P_solution = P_vap_pure - Delta_P;
        const Delta_T_b = i * K_b * m;
        const T_b_solution = T_b_pure + Delta_T_b;
        const Delta_T_f = i * K_f * m;
        const T_f_solution = T_f_pure - Delta_T_f;
        // Osmotic pressure (approximation: molarity ≈ molality for dilute solutions)
        const C = m; // Approximation for dilute aqueous solutions
        const Pi = i * C * R * T;
        return {
            x_solute,
            x_solvent,
            Delta_P,
            P_solution,
            Delta_T_b,
            T_b_solution,
            Delta_T_f,
            T_f_solution,
            Pi,
            Pi_atm: Pi / STANDARD_PRESSURE,
        };
    },
    failureModes: [
        {
            id: 'negative-molality',
            name: 'Negative Molality',
            check: (_state, _params) => {
                const m = getState(getStateValues(_state), 'm');
                return m < 0;
            },
            severity: 'error',
            educationalMessage: 'Molality cannot be negative.',
        },
        {
            id: 'high-concentration',
            name: 'High Concentration Limit',
            check: (_state, _params) => {
                const m = getState(getStateValues(_state), 'm');
                return m > 10;
            },
            severity: 'warning',
            educationalMessage: 'At high concentrations, the approximations used (ideal solution behavior, molarity ≈ molality) break down. Activity coefficients would be needed for accurate predictions.',
        },
    ],
    bounds: {
        m: { min: 0, max: 20 },
    },
});
//# sourceMappingURL=physical-chemistry.js.map